import { useQuery } from '@tanstack/react-query';

import { listWorkspaceFiles } from '@/lib/api/generated/api';
import { isFolderPlaceholder } from '../folders';
import { fileKeys } from '../queryKeys';

/**
 * One directory's listing.
 *
 * The contract only distinguishes `200` (found) from `422` (the path does
 * not resolve to a directory — validation failure per contracts/files-api.md)
 * — there is no separate "not found" status. A `422` therefore resolves to
 * `null` rather than throwing, the same asymmetry `workspaceQueryOptions`
 * uses for a workspace read: a page can tell "this path does not exist" (the
 * not-found state) from "the request itself never came back" (`loadFailed`,
 * a retry surface — network failure, backend down). Collapsing the two would
 * tell a stale deep link "temporarily unavailable" instead of "not found", or
 * offer a "try again" that can never succeed for a path that was renamed.
 */
export function useWorkspaceFileListing(workspaceId: string, path: string) {
  const query = useQuery({
    queryKey: fileKeys.listing(workspaceId, path),
    queryFn: async () => {
      const response = await listWorkspaceFiles(workspaceId, { path });
      if (response.status !== 200) return null;
      // The placeholder holding an empty folder's prefix open is not a file
      // anybody put here, and an otherwise-empty folder must still read as
      // empty (`folders.ts`).
      return {
        ...response.data,
        entries: response.data.entries.filter(
          (entry) => !isFolderPlaceholder(entry),
        ),
      };
    },
    retry: false,
  });

  return {
    listing: query.data,
    isPending: query.isPending,
    loadFailed: !query.isPending && query.isError,
    refetch: query.refetch,
  };
}
